import React, { useEffect, useState, useCallback } from "react";
import { groupItems } from "../../../utils/printManager";
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  CreditCard,
  Banknote,
  AlertTriangle,
  MessageSquare,
  MessageCircle,
  Printer,
  FileText,
  Tag,
  Trash2,
  RotateCcw,
} from "lucide-react";
import {
  getSalesOrderDetail,
  requestOrderCancellation,
  approveOrderCancellation,
  voidOrderItem,
} from "../../../api/salesService";
import { returnService } from "../../../api/returnService";
import { authService } from "../../../api/authService";
import { configService } from "../../../api/configService";
import { salesTypeService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";
import { buildReceiptHtml, buildOrderReceiptHtml, getReceiptConfig, filterLabelItems, detectVehicleUnit } from "../../../utils/receiptConfig";
import { executePrint, executePrintLabel } from "../../../utils/printManager";
import ReturnSuccessModal from "../ReturnSuccessModal";

const STATUS_CONFIG = {
  DONE: {
    label: "Selesai",
    icon: CheckCircle,
    cls: "text-green-600",
    badge: "bg-green-50 text-green-700 border-green-200",
  },
  RETURNED: {
    label: "Diretur Penuh",
    icon: RotateCcw,
    cls: "text-purple-600",
    badge: "bg-purple-50 text-purple-700 border-purple-200",
  },
  PARTIAL_RETURN: {
    label: "Sebagian Diretur",
    icon: RotateCcw,
    cls: "text-amber-600",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
  },
  PENDING: {
    label: "Pending",
    icon: Clock,
    cls: "text-amber-600",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
  },
  CANCEL_REQUESTED: {
    label: "Pengajuan Batal",
    icon: XCircle,
    cls: "text-teal-600",
    badge: "bg-teal-50 text-teal-700 border-teal-200",
  },
  CANCELLED: {
    label: "Dibatalkan",
    icon: XCircle,
    cls: "text-red-600",
    badge: "bg-red-50 text-red-700 border-red-200",
  },
};

const PAYMENT_STATUS_CONFIG = {
  PAID: { label: "Lunas", cls: "bg-green-50 text-green-700 border-green-200" },
  PARTIAL: {
    label: "Partial",
    cls: "bg-yellow-50 text-yellow-700 border-yellow-200",
  },
  UNPAID: {
    label: "Belum Bayar",
    cls: "bg-red-50 text-red-700 border-red-200",
  },
  REFUNDED: {
    label: "Dikembalikan",
    cls: "bg-gray-100 text-gray-700 border-gray-200",
  },
};

const PAYMENT_METHOD_LABEL = {
  CASH: "Tunai",
  QRIS: "QRIS",
  TRANSFER: "Transfer",
  MIXED: "Campuran",
};
const SELLER_LABEL = {
  INTERNAL: "Internal",
  PUBLIC: "Pelanggan Umum",
};

const isHideDiscount = (d, salesTypes = []) => {
  const flag = d?.discount_from_type ?? d?.totals?.discount_from_type ?? d?.meta?.discount_from_type;
  if (flag !== undefined) return !!flag;

  const sellerName = String(d?.seller_type || "").trim();
  if (sellerName && salesTypes.length > 0) {
    const matched = salesTypes.find(
      (t) => String(t.name || "").trim().toLowerCase() === sellerName.toLowerCase()
    );
    if (matched) return matched.jenis === "Diskon";
  }
  return false;
};

const isHideMarkup = (d, salesTypes = []) => {
  const flag = d?.discount_from_type ?? d?.totals?.discount_from_type ?? d?.meta?.discount_from_type;
  if (flag !== undefined) return !!flag;

  const sellerName = String(d?.seller_type || "").trim();
  if (sellerName && salesTypes.length > 0) {
    const matched = salesTypes.find(
      (t) => String(t.name || "").trim().toLowerCase() === sellerName.toLowerCase()
    );
    if (matched) return matched.jenis === "Markup";
  }
  return false;
};

// True jika diskon/markup OTOMATIS dari tipe penjualan (hide di nota)
const isAutoFromSaleType = (d, salesTypes = []) => {
  const flag = d?.discount_from_type ?? d?.totals?.discount_from_type ?? d?.meta?.discount_from_type;
  if (flag !== undefined) return !!flag;

  const sellerName = String(d?.seller_type || "").trim();
  if (sellerName && salesTypes.length > 0) {
    const matched = salesTypes.find(
      (t) => String(t.name || "").trim().toLowerCase() === sellerName.toLowerCase()
    );
    if (matched) return matched.jenis === "Diskon" || matched.jenis === "Markup";
  }
  return false;
};

function fmt(n) {
  return "Rp " + (n ?? 0).toLocaleString("id-ID");
}

function formatDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function TransactionDetailModal({
  transaction,
  onClose,
  onRefresh,
}) {
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Inline cancellation state
  const [cancelMode, setCancelMode] = useState(null); // 'request' | 'approve' | null
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelDone, setCancelDone] = useState(false);
  
  const [itemToVoid, setItemToVoid] = useState(null);
  const [voidLoading, setVoidLoading] = useState(false);
  const [successReturnData, setSuccessReturnData] = useState(null);

  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "Toko",
    address: "",
    phone: "",
    logoUrl: "",
    footerNote: "",
  });
  const [salesTypes, setSalesTypes] = useState([]);
  const { showSuccess, showError } = useNotification();

  const fetchDetail = useCallback(() => {
    if (!transaction?.id) return;
    setLoading(true);
    setError(null);
    getSalesOrderDetail(transaction.id)
      .then((res) => setDetail(res?.data ?? res))
      .catch((err) => setError(err.message || "Gagal memuat detail transaksi"))
      .finally(() => setLoading(false));
  }, [transaction?.id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    let mounted = true;
    const loadStoreInfo = async () => {
      try {
        const response = await configService.getStore();
        const store = response?.data ?? response ?? {};
        if (!mounted) return;
        setStoreInfo({
          name: store?.name || localStorage.getItem("tenantName") || "Toko",
          address: store?.settings?.address || store?.address || "",
          phone: store?.settings?.phone || store?.phone || "",
          logoUrl: store?.logo_url || "",
          footerNote: store?.settings?.footer_note || "",
        });
      } catch {
        if (!mounted) return;
        setStoreInfo((prev) => ({
          ...prev,
          name: localStorage.getItem("tenantName") || prev.name || "Toko",
        }));
      }
    };

    loadStoreInfo();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    salesTypeService.list({ limit: 200 })
      .then((res) => {
        const list = res?.data ?? res ?? [];
        if (mounted) setSalesTypes(Array.isArray(list) ? list : (list.data ?? []));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!transaction) return null;

  const d = detail;
  const sanitizedNotes = (d?.notes || "")
    .replace(/^\s*\[?\s*CANCEL\s+REQUEST\s*\]?\s*:?\s*/i, "")
    .replace(/^\s*Meja:\s*[^\n,-]+\s*(?:-\s*)?/i, "")
    .trim();
  const statusInfo = STATUS_CONFIG[d?.status] ?? STATUS_CONFIG.PENDING;
  const StatusIcon = statusInfo.icon;
  const paymentStatusInfo = PAYMENT_STATUS_CONFIG[d?.payment_status] ?? {
    label: d?.payment_status,
    cls: "bg-gray-100 text-gray-700 border-gray-200",
  };

  const subtotal = (d?.items ?? []).reduce(
    (s, i) => s + (i.total_price ?? 0),
    0,
  );
  const additionalCosts = d?.additional_costs ?? 0;

  // RBAC
  const canRequestCancel = authService.hasPermission("pos.cancel_trx");
  const canApproveCancel = authService.hasPermission("pos.approve_cancel");

  const handleCancelSubmit = async () => {
    if (cancelMode === "request" && !cancelReason.trim()) {
      setCancelError("Alasan pembatalan wajib diisi.");
      return;
    }
    try {
      setCancelLoading(true);
      setCancelError(null);
      if (cancelMode === "request") {
        await requestOrderCancellation(transaction.id, {
          reason: cancelReason.trim(),
        });
        showSuccess("Pengajuan pembatalan berhasil dikirim.");
      } else {
        await approveOrderCancellation(transaction.id);
        showSuccess("Pembatalan transaksi berhasil disetujui.");
      }
      setCancelDone(true);
      onRefresh?.();
      // Refresh detail after a brief moment to show updated status
      setTimeout(() => {
        setCancelMode(null);
        setCancelDone(false);
        setCancelReason("");
        fetchDetail();
      }, 1200);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Gagal membatalkan transaksi.";
      setCancelError(msg);
      showError(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleConfirmVoidItem = async () => {
    if (!itemToVoid) return;
    try {
      setVoidLoading(true);
      await voidOrderItem(transaction.id, itemToVoid.id);
      showSuccess(`Item ${itemToVoid.item_name} berhasil di-void`);
      setItemToVoid(null);
      // Refresh modal data
      fetchDetail();
      // Also refresh parent table
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err.response?.data?.message || err.message || "Gagal membatalkan item.");
    } finally {
      setVoidLoading(false);
    }
  };

  const normalizePhone = (phone = "") => phone.replace(/[^\d+]/g, "");

  const handleSendWhatsapp = () => {
    if (!d) return;
    const rawPhone = d.customer_phone || d.customer?.phone || "";
    const phone = normalizePhone(rawPhone);
    if (!phone) {
      showError("Nomor WhatsApp pelanggan tidak tersedia.");
      return;
    }

    const total = fmt(d.grand_total || 0);
    const message = [
      "Halo, berikut detail transaksi Anda:",
      `No. Transaksi: ${d.transaction_number || "-"}`,
      `Tanggal: ${formatDateTime(d.order_date)}`,
      `Total: ${total}`,
      "Terima kasih telah bertransaksi.",
    ].join("\n");

    const waWindow = window.open(
      `https://wa.me/${encodeURIComponent(phone)}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
    if (!waWindow) {
      showError("Popup tab baru diblokir browser");
    }
  };

  const handleViewReturnReceipt = async () => {
    try {
      const res = await returnService.listReturns({ search: d?.transaction_number, limit: 1 });
      const list = res?.data?.data || res?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        const detailRes = await returnService.getReturnDetail(list[0].id);
        setSuccessReturnData(detailRes?.data ?? detailRes ?? list[0]);
      } else {
        setSuccessReturnData({
          return_number: "RTR-" + (d?.transaction_number?.replace("TRX-", "") || ""),
          transaction_number: d?.transaction_number || "-",
          return_date: d?.updated_at || d?.created_at || new Date().toISOString(),
          customer_name: d?.customer_name || "Pelanggan Umum",
          refund_method: "Tunai",
          reason: "Retur Penjualan",
          total_refund_amount: d?.grand_total || d?.subtotal || 0,
          items: (d?.items || []).map((itm) => ({
            item_name: itm.item_name || itm.name || "Item",
            quantity: itm.quantity || 1,
            unit_price: itm.unit_price || itm.price || 0,
            total_price: itm.total_price ?? ((itm.unit_price || itm.price || 0) * (itm.quantity || 1)),
          })),
        });
      }
    } catch (err) {
      console.error("Gagal memuat bukti retur:", err);
    }
  };

  const handleOpenReceipt = () => {
    if (!d) return;

    try {
      const items = (d.items || []).map((item) => {
        const name = item.item_name || item.name || "-";
        const qty = Number(item.quantity || 0);
        const price = Number(item.unit_price || 0);
        const total = Number(item.total_price ?? qty * price);
        const rawUnit = item.unit_name || item.unit?.name || (typeof item.unit === "string" ? item.unit : "");
        const physicalSatuan =
          item.satuan ||
          item.product_unit_name ||
          item.selling_unit_name ||
          item.selling_unit?.name ||
          (typeof item.selling_unit === "string" ? item.selling_unit : "") ||
          item.product?.unit ||
          item.product?.unit_name ||
          "";
        return {
          name,
          qty,
          price,
          total,
          discount_amount: Number(item.discount_amount || 0),
          additional_cost: Number(item.additional_cost || 0),
          unit: rawUnit || physicalSatuan || "",
          unit_name: rawUnit || "",
          asset_unit_name: item.asset_unit_name || "",
          satuan: physicalSatuan || "",
          product_id: item.product_id,
          product_variant_id: item.product_variant_id,
          product_addon_id: item.product_addon_id,
          variant_name: item.variant_name,
          notes: item.notes || item.note || "",
          item_type: item.item_type || "PRODUCT",
        };
      });

      const grandTotal = Number(d.grand_total || 0);
      const subtotalVal = Number(subtotal || grandTotal);

      let tunaiAmount = Number(d.total_paid ?? grandTotal);
      let dpAmount = 0;
      const paymentsArr = Array.isArray(d.payments) ? d.payments : [];

      if (paymentsArr.length > 1) {
        const sorted = [...paymentsArr].sort((a, b) => (a.payment_sequence || 0) - (b.payment_sequence || 0));
        const last = sorted[sorted.length - 1];
        const lastAmount = last.amount != null
          ? Number(last.amount)
          : (Number(last.cash_amount || 0) + Number(last.electronic_amount || 0));
        dpAmount = tunaiAmount - lastAmount;
        tunaiAmount = lastAmount;
      }

      // Tentukan label metode pembayaran terakhir. Untuk transaksi DP,
      // metode pembayaran terakhir (yg dipakai membayar nominal `tunaiAmount`)
      // adalah yang paling relevan untuk ditampilkan di nota.
      const lastPayment =
        paymentsArr.length > 0
          ? [...paymentsArr].sort(
              (a, b) => (a.payment_sequence || 0) - (b.payment_sequence || 0),
            )[paymentsArr.length - 1]
          : null;
      const lastMethod = (
        lastPayment?.payment_method ||
        lastPayment?.method ||
        d.payment_method ||
        ""
      ).toUpperCase();
      const paymentMethodLabel =
        ({
          CASH: "Tunai",
          TRANSFER: "Transfer",
          QRIS: "QRIS",
          MIXED: "Campuran",
        })[lastMethod] || "Tunai";
      const paidAt =
        (d.payments && d.payments[0]?.payment_date
          ? formatDateTime(d.payments[0].payment_date)
          : null) || formatDateTime(d.order_date);

      const receiptData = {
        title: `Nota ${d.transaction_number || "-"}`,
        storeName: storeInfo?.name || localStorage.getItem("tenantName") || "Toko",
        storeAddress: storeInfo?.address || "",
        storePhone: storeInfo?.phone || "",
        storeLogoUrl: storeInfo?.logoUrl || localStorage.getItem("tenantLogoUrl") || "",
        storeFooterNote: storeInfo?.footerNote || "Terima Kasih Telah Menjadi Pelanggan Kami",
        meta: {
          transactionNumber: d.transaction_number || "-",
          transactionDateTime: formatDateTime(d.order_date),
          queueNumber: d.queue_number || d.no_urut,
          cashierName: d.created_by || d.cashier_name || "-",
          customerName: d.customer_name || "Walk-in",
          customerPhone: d.customer_phone || d.customer?.phone || d.customer?.phone_number || "",
          customerAddress: d.customer_address || d.customer?.address || "",
          unitName: d.unit_name || d.asset_unit_name || detectVehicleUnit(d, d.items) || "",
          sellerType: SELLER_LABEL[d.seller_type] ?? d.seller_type ?? "-",
          tableName: d.table_name || d.selected_table_name || d.table?.name || (d.notes && d.notes.match(/Meja:\s*([^\n,-]+)/)?.[1]?.trim()) || "",
          status: STATUS_CONFIG[d.status]?.label ?? d.status ?? "-",
          transactionNotes: d.notes || d.note || "",
        },
        items,
        totals: {
          subtotal: subtotalVal,
          discount: Number(d.discount_total ?? d.discount ?? 0),
          additionalCosts: Number(d.additional_costs ?? d.additional_cost ?? 0),
          total: grandTotal,
          dpAmount: dpAmount,
          tunai: tunaiAmount,
          totalBayar: grandTotal,
          payments: paymentsArr,
          paymentMethodLabel,
          change: Math.max(tunaiAmount - Math.max(0, grandTotal - dpAmount), 0),
          isDownPayment:
            d.status === "PENDING" &&
            d.payment_status === "PARTIAL" &&
            (tunaiAmount + dpAmount) > 0 &&
            (tunaiAmount + dpAmount) < grandTotal,
          remaining: Math.max(grandTotal - (tunaiAmount + dpAmount), 0),
          discount_from_type: isAutoFromSaleType(d, salesTypes),
        },
        print: {
          paidAt,
          printedBy: d.created_by || d.cashier_name || "-",
        },
      };

      executePrint(receiptData, 'transaction')
        .then((res) => {
          if (res.method === 'bluetooth') {
             showSuccess("Struk berhasil dikirim ke printer bluetooth");
          }
        })
        .catch((err) => {
          showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
        });
    } catch (err) {
      console.error("Gagal memproses cetak nota:", err);
      showError("Gagal memproses cetak nota: " + (err?.message || err));
    }
  };

  const handleOpenOrderReceipt = () => {
    if (!d) return;

    try {
      const items = (d.items || []).map((item) => {
        const name = item.item_name || item.name || "-";
        const qty = Number(item.quantity || 0);
        return {
          name,
          qty,
          category_id: item.category_id || item.category || null,
          product_id: item.product_id,
          product_variant_id: item.product_variant_id,
          product_addon_id: item.product_addon_id,
          variant_name: item.variant_name,
          notes: item.notes || item.note || "",
          item_type: item.item_type || "PRODUCT",
        };
      });

      const receiptData = {
        storeName: storeInfo?.name || localStorage.getItem("tenantName") || "Toko",
        storeFooterNote: storeInfo?.footerNote || "Dicetak untuk persiapan pesanan",
        meta: {
          transactionNumber: d.transaction_number || "-",
          transactionDateTime: formatDateTime(d.order_date),
          cashierName: d.created_by || d.cashier_name || "-",
          sellerType: SELLER_LABEL[d.seller_type] ?? d.seller_type ?? "-",
          tableName: d.table_name || d.selected_table_name || d.table?.name || (d.notes && d.notes.match(/Meja:\s*([^\n,-]+)/)?.[1]?.trim()) || "",
          customerName: d.customer_name || "-",
          unitName: d.unit_name || d.asset_unit_name || detectVehicleUnit(d, d.items) || "",
          transactionNotes: d.notes || d.note || "",
        },
        items,
      };

      executePrint(receiptData, 'order')
        .then((res) => {
          if (res.method === 'bluetooth') {
             showSuccess("Nota pesanan berhasil dikirim ke printer bluetooth");
          }
        })
        .catch((err) => {
          showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
        });
    } catch (err) {
      console.error("Gagal memproses nota pesanan:", err);
      showError("Gagal mencetak nota pesanan: " + (err?.message || err));
    }
  };

  const handlePrintLabels = () => {
    if (!d) return;
    const txDate = d.order_date ? new Date(d.order_date) : new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const datetime = `${pad(txDate.getDate())}/${pad(txDate.getMonth()+1)}/${String(txDate.getFullYear()).slice(2)} ${pad(txDate.getHours())}:${pad(txDate.getMinutes())}`;

    const storeName = storeInfo?.name || localStorage.getItem("tenantName") || "Toko";
    const customerName = d.customer_name || "Pelanggan";
    const salesTypeName = SELLER_LABEL[d.seller_type] ?? d.seller_type ?? "";

    const printConfig = getReceiptConfig();
    const rawItems = (d.items || []).filter((it) => !it.product_addon_id);
    const filteredItems = filterLabelItems(rawItems, printConfig?.labelCategories);

    if (!filteredItems || filteredItems.length === 0) {
      showError("Tidak ada produk dalam kategori label yang dipilih untuk dicetak");
      return;
    }

    const expanded = [];
    filteredItems.forEach((it) => {
      const qty = Number(it.quantity || it.qty || 1);
      const unit = it.unit || it.unit_name || "";
      const productName = it.item_name || it.product_name || it.name || "-";
      const variantName = it.variant_name || "";
      const notes = it.notes || it.note || it.catatan || it.item_note || it.product_note || it.description || "";
      const addons = (d.items || [])
        .filter((a) => a.product_addon_id && a.product_id === it.product_id)
        .map((a) => ({ name: a.item_name || a.name || "", qty: Number(a.quantity || a.qty || 1) }));
      for (let i = 0; i < qty; i++) {
        expanded.push({ storeName, customerName, salesTypeName, productName, variantName, qty: 1, unit, notes, addons, datetime });
      }
    });

    const total = expanded.length;
    const labelDataArray = expanded.map((e, idx) => ({ ...e, index: idx + 1, total }));

    if (!labelDataArray.length) return;
    executePrintLabel(labelDataArray)
      .then((res) => {
        if (res.method === "bluetooth") showSuccess("Label pesanan berhasil dikirim ke printer bluetooth");
      })
      .catch((err) => showError(err?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer."));
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[92vh] modal-panel-enter">
        {/* Header */}
        <div className="bg-teal-600 px-6 py-4 flex justify-between items-center rounded-t-xl shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Detail Transaksi</h2>
            {d && (
              <p className="text-teal-200 text-xs mt-0.5 font-mono">
                {d.transaction_number}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-teal-700 p-1.5 rounded-full transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              <p className="text-sm text-gray-500">
                Memuat detail transaksi...
              </p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Content */}
          {!loading && !error && d && (
            <>
              {/* Status & Nomor */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-4 border-b">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusIcon className={`w-5 h-5 shrink-0 ${statusInfo.cls}`} />
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusInfo.badge}`}>
                    {statusInfo.label}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${paymentStatusInfo.cls}`}>
                    {paymentStatusInfo.label}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border border-slate-200 bg-slate-50 text-slate-700">
                    {SELLER_LABEL[d.seller_type] ?? d.seller_type ?? "-"}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-mono shrink-0">
                  {formatDateTime(d.order_date)}
                </p>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Nama Pelanggan</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {d.customer_name || "Walk-in"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Kasir</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {d.payments?.[0]?.created_by_user?.name ||
                      d.payments?.[0]?.created_by_user?.username ||
                      d.updated_by_user?.name ||
                      d.updated_by_user?.username ||
                      d.created_by_user?.name ||
                      d.created_by_user?.username ||
                      (typeof d.updated_by === "string" && d.updated_by.length < 30 ? d.updated_by : "") ||
                      (typeof d.created_by === "string" && d.created_by.length < 30 ? d.created_by : "") ||
                      d.cashier_name ||
                      (d.source === "ECATALOG_PUBLIC" ? "E-Katalog" : "-")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Jumlah Item</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {d.item_count ?? d.items?.length ?? 0} item
                  </p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Daftar Produk
                </h3>
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 grid grid-cols-12 text-xs font-medium text-gray-500 border-b">
                    <span className="col-span-5">Nama Produk</span>
                    <span className="col-span-1 text-center">Qty</span>
                    <span className="col-span-2 text-right">Harga</span>
                    <span className="col-span-2 text-right">Total</span>
                    <span className="col-span-2 text-center">Aksi</span>
                  </div>
                  <div className="divide-y">
                    {groupItems(d.items ?? []).map((item) => (
                      <React.Fragment key={item.id}>
                        <div
                          className="px-4 py-3 grid grid-cols-12 items-center text-sm">
                          <div className="col-span-5">
                            <p className="font-semibold text-gray-800">
                              {item.name || item.item_name}
                            </p>
                            {item.variant_name && (
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                Varian: {item.variant_name}
                              </p>
                            )}
                            {(item.unit_name ||
                              item.unit?.name ||
                              item.asset_unit_name ||
                              (typeof item.unit === "string" && item.unit)) && (
                              <p className="text-[11px] text-teal-700 mt-0.5 inline-flex items-center gap-1 bg-teal-50 ring-1 ring-teal-200 px-1.5 py-0.5 rounded">
                                Unit:{" "}
                                {item.unit_name ||
                                  item.unit?.name ||
                                  item.asset_unit_name ||
                                  item.unit}
                              </p>
                            )}
                            {(item.notes || item.note) && (
                              <p className="text-[11px] text-amber-700 italic font-medium mt-1">
                                * Catatan: {item.notes || item.note}
                              </p>
                            )}
                          </div>
                          <p className="col-span-1 text-center text-gray-600">
                            {item.quantity || item.qty}
                          </p>
                          <p className="col-span-2 text-right text-gray-600">
                            {fmt(item.unit_price || item.price)}
                          </p>
                          <p className="col-span-2 text-right font-semibold text-gray-800">
                            {fmt(item.total_price || item.total)}
                          </p>
                          <div className="col-span-2 flex justify-center">
                            {d.status !== "CANCELLED" && (
                              <button
                                type="button"
                                onClick={() => setItemToVoid(item)}
                                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-red-200 hover:border-red-300"
                                title="Void / Hapus Item">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        {Number(item.discount_amount || 0) > 0 && (
                          <div className="px-4 py-1.5 pl-12 flex justify-between text-xs text-rose-600 bg-rose-50/40 border-t border-gray-100">
                            <span>Diskon Item</span>
                            <span>- {fmt(item.discount_amount)}</span>
                          </div>
                        )}
                        {Number(item.additional_cost || 0) > 0 && (
                          <div className="px-4 py-1.5 pl-12 flex justify-between text-xs text-blue-600 bg-blue-50/40 border-t border-gray-100">
                            <span>Biaya Tambahan Item</span>
                            <span>+ {fmt(item.additional_cost)}</span>
                          </div>
                        )}
                        {item.addons && item.addons.map((addon) => (
                          <div
                            key={addon.id}
                            className="px-4 py-2 pl-12 grid grid-cols-12 items-center text-xs bg-slate-50/50 border-t border-gray-100">
                            <div className="col-span-5">
                              <span className="text-slate-600 font-medium italic">+ {addon.name}</span>
                            </div>
                            <p className="col-span-1 text-center text-slate-500">
                              {addon.qty}
                            </p>
                            <p className="col-span-2 text-right text-slate-500">
                              {fmt(addon.price)}
                            </p>
                            <p className="col-span-2 text-right font-semibold text-slate-700">
                              {fmt(addon.total)}
                            </p>
                            <div className="col-span-2 flex justify-center">
                              {d.status !== "CANCELLED" && (
                                <button
                                  type="button"
                                  onClick={() => setItemToVoid(addon)}
                                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors border border-red-100"
                                  title="Void / Hapus Item Extra">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              {/* Totals */}
              <div className="border rounded-xl p-4 space-y-2.5 bg-gray-50">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Sub Total</span>
                  <span className="font-medium text-gray-800">
                    {fmt(subtotal)}
                  </span>
                </div>
                {/* [HIDDEN_SALE_TYPE_MARKUP]: Hide additional costs if automatic sale type markup */}
                {additionalCosts > 0 && !isHideMarkup(d, salesTypes) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Biaya Tambahan</span>
                    <span className="font-medium text-gray-800">
                      {fmt(additionalCosts)}
                    </span>
                  </div>
                )}
                {/* [HIDDEN_SALE_TYPE_DISCOUNT]: Hide discount row if automatic sale type discount */}
                {(d.discount_total ?? 0) > 0 && !isHideDiscount(d, salesTypes) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Diskon</span>
                    <span className="font-medium text-red-600">
                      - {fmt(d.discount_total)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                  <span className="text-teal-700">Grand Total</span>
                  <span className="text-teal-700">{fmt(d.grand_total)}</span>
                </div>
              </div>

              {/* Payments */}
              {(d.payments ?? []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Pembayaran
                  </h3>
                  <div className="space-y-2">
                    {d.payments.map((pay) => (
                      <div
                        key={pay.id}
                        className="flex flex-col border rounded-lg px-4 py-3 bg-white">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
                              {pay.payment_method === "CASH" ? (
                                <Banknote className="w-4 h-4 text-teal-600" />
                              ) : (
                                <CreditCard className="w-4 h-4 text-teal-600" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {PAYMENT_METHOD_LABEL[pay.payment_method] ??
                                  pay.payment_method}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDateTime(pay.payment_date)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-800">
                              {fmt(pay.cash_amount || pay.electronic_amount || 0)}
                            </p>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full border ${
                                pay.payment_status === "COMPLETED"
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : "bg-gray-100 text-gray-600 border-gray-200"
                              }`}>
                              {pay.payment_status === "COMPLETED"
                                ? "Selesai"
                                : pay.payment_status}
                            </span>
                          </div>
                        </div>
                        {pay.payment_proof_url && (
                          <div className="mt-3 pt-3 border-t border-dashed border-gray-200 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setPreviewImage(pay.payment_proof_url)}
                              className="text-teal-600 hover:text-teal-700 text-xs font-medium flex items-center gap-1.5 transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Lihat Bukti Transfer
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm mt-3 px-1">
                    <span className="text-gray-500">Total Dibayar</span>
                    <span className="font-semibold text-gray-800">
                      {fmt(d.total_paid)}
                    </span>
                  </div>
                  {(d.total_paid ?? 0) > (d.grand_total ?? 0) && (
                    <div className="flex justify-between text-sm px-1">
                      <span className="text-gray-500">Kembalian</span>
                      <span className="font-semibold text-green-600">
                        {fmt((d.total_paid ?? 0) - (d.grand_total ?? 0))}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Notes (includes [CANCEL REQUEST] prefix appended by backend) */}
              {sanitizedNotes && (
                <div className="border rounded-xl p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-amber-600" />
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      Catatan
                    </p>
                  </div>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap">
                    {sanitizedNotes}
                  </p>
                </div>
              )}

              {/* ── Cancellation Actions ── */}
              {cancelMode === null ? (
                <div className="flex gap-3 pt-2">
                  {d.status === "PENDING" && canRequestCancel && (
                    <button
                      onClick={() => setCancelMode("request")}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-orange-50 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors">
                      <XCircle className="w-4 h-4" />
                      Ajukan Pembatalan
                    </button>
                  )}
                  {d.status === "CANCEL_REQUESTED" && canApproveCancel && (
                    <button
                      onClick={() => setCancelMode("approve")}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                      <CheckCircle className="w-4 h-4" />
                      Approve Pembatalan
                    </button>
                  )}
                </div>
              ) : cancelDone ? (
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  {cancelMode === "request"
                    ? "Pengajuan pembatalan berhasil dikirim."
                    : "Pembatalan berhasil disetujui."}
                </div>
              ) : (
                <div
                  className={`border rounded-xl p-4 space-y-3 ${
                    cancelMode === "approve"
                      ? "bg-red-50 border-red-200"
                      : "bg-orange-50 border-orange-200"
                  }`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle
                      className={`w-4 h-4 shrink-0 ${
                        cancelMode === "approve"
                          ? "text-red-600"
                          : "text-orange-600"
                      }`}
                    />
                    <p
                      className={`text-sm font-semibold ${
                        cancelMode === "approve"
                          ? "text-red-700"
                          : "text-orange-700"
                      }`}>
                      {cancelMode === "request"
                        ? "Ajukan Pembatalan"
                        : "Konfirmasi Approve Pembatalan"}
                    </p>
                  </div>

                  {cancelMode === "request" && (
                    <>
                      <p className="text-xs text-orange-700">
                        Status transaksi akan berubah menjadi{" "}
                        <strong>Menunggu Persetujuan</strong>.
                      </p>
                      <textarea
                        className="w-full p-3 border border-orange-300 bg-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 h-20 resize-none"
                        placeholder="Alasan pembatalan..."
                        value={cancelReason}
                        onChange={(e) => {
                          setCancelReason(e.target.value);
                          setCancelError(null);
                        }}
                        disabled={cancelLoading}
                      />
                    </>
                  )}

                  {cancelMode === "approve" && (
                    <p className="text-xs text-red-700">
                      Transaksi ini akan <strong>dibatalkan permanen</strong>.
                      Tindakan tidak dapat diurungkan.
                    </p>
                  )}

                  {cancelError && (
                    <p className="text-xs text-red-600 bg-red-100 rounded-lg px-3 py-2">
                      {cancelError}
                    </p>
                  )}

                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => {
                        setCancelMode(null);
                        setCancelError(null);
                        setCancelReason("");
                      }}
                      disabled={cancelLoading}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">
                      Batal
                    </button>
                    <button
                      onClick={handleCancelSubmit}
                      disabled={cancelLoading}
                      className={`px-3 py-1.5 text-xs font-medium text-white rounded-lg flex items-center gap-1.5 disabled:opacity-60 ${
                        cancelMode === "approve"
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-orange-500 hover:bg-orange-600"
                      }`}>
                      {cancelLoading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Memproses...</span>
                        </>
                      ) : cancelMode === "request" ? (
                        "Kirim Pengajuan"
                      ) : (
                        "Setujui Pembatalan"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* === FOOTER === */}
        <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50/80 rounded-b-xl">
          {/* Baris Tombol Aksi */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-end gap-2">
            {(d?.status === "RETURNED" || d?.status === "PARTIAL_RETURN") && (
              <button
                onClick={handleViewReturnReceipt}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 bg-purple-600 text-white rounded-xl text-xs font-semibold hover:bg-purple-700 transition-colors shadow-sm">
                <RotateCcw className="w-3.5 h-3.5" />
                Lihat Bukti Retur
              </button>
            )}
            <button
              onClick={() => {
                const text = encodeURIComponent(
                  `Halo ${d?.customer_name || "Kak"}, ini nota transaksi untuk pesanan Anda. Terima kasih!`
                );
                window.open(`https://wa.me/?text=${text}`, "_blank");
              }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 bg-emerald-600 text-white rounded-xl text-xs font-semibold hover:bg-emerald-700 transition-colors shadow-sm">
              <MessageCircle className="w-3.5 h-3.5" />
              Kirim WA
            </button>
            <button
              onClick={handleOpenReceipt}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 bg-orange-500 text-white rounded-xl text-xs font-semibold hover:bg-orange-600 transition-colors shadow-sm">
              <Printer className="w-3.5 h-3.5" />
              Nota Bayar
            </button>
            {isApps && (
              <button
                onClick={handleOpenOrderReceipt}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 border-2 border-teal-500 bg-white text-teal-600 rounded-xl text-xs font-semibold hover:bg-teal-50 transition-colors">
                <FileText className="w-3.5 h-3.5 text-teal-500" />
                Nota Pesanan
              </button>
            )}
            {isApps && (
              <button
                onClick={handlePrintLabels}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 border-2 border-emerald-500 bg-white text-emerald-700 rounded-xl text-xs font-semibold hover:bg-emerald-50 transition-colors">
                <Tag className="w-3.5 h-3.5 text-emerald-600" />
                Label Pesanan
              </button>
            )}
          </div>
        </div>
      </div>

      {successReturnData && (
        <ReturnSuccessModal
          returnData={successReturnData}
          onClose={() => setSuccessReturnData(null)}
        />
      )}

      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation();
            setPreviewImage(null);
          }}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewImage(null);
              }}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={previewImage} 
              alt="Preview Bukti Transfer" 
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Item Void Confirmation Modal */}
      {itemToVoid && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !voidLoading && setItemToVoid(null)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                Void Item
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Apakah Anda yakin ingin membatalkan item <span className="font-semibold">{itemToVoid.item_name}</span>? 
                Total transaksi akan dikurangi dan nota akan masuk laporan void.
              </p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setItemToVoid(null)}
                  disabled={voidLoading}
                  className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50">
                  Batal
                </button>
                <button
                  type="button"
                  onClick={handleConfirmVoidItem}
                  disabled={voidLoading}
                  className="flex-1 py-2.5 px-4 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {voidLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Memproses...</span>
                    </>
                  ) : (
                    "Ya, Void Item"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
